import * as libRegions from '@luxuryescapes/lib-regions';
import { Brand } from '@luxuryescapes/lib-regions/lib/regions';

import { allRegions } from '../../../../consts/allRegions';
import { brandsByValue, ledBrands } from '../../../../consts/brands';
import { AVAILABILITY, ONLINE_PURCHASE } from '../../../../consts/schedule';
import { getLatestEnd } from '../../../../utils/scheduleForType';

import { ScheduleUpdateResult } from './types';

/**
 * Returns true if the given LIST_VISIBILITY end date exceeds the latest
 * AVAILABILITY or ONLINE_PURCHASE end date, meaning the user should set
 * the book-by / offline-purchase date first.
 */
export function hasListVisibilityDateConflict(endDate: string | Date, allSchedules: Array<App.Schedule>): boolean {
  const availabilitySchedules = allSchedules.filter((s) => s.type === AVAILABILITY);
  const onlinePurchaseSchedules = allSchedules.filter((s) => s.type === ONLINE_PURCHASE);
  const availabilityEnd = availabilitySchedules.length > 0 ? getLatestEnd(availabilitySchedules) : null;
  const onlinePurchaseEnd = onlinePurchaseSchedules.length > 0 ? getLatestEnd(onlinePurchaseSchedules) : null;

  return (
    (!!availabilityEnd && new Date(endDate) > new Date(availabilityEnd)) ||
    (!!onlinePurchaseEnd && new Date(endDate) > new Date(onlinePurchaseEnd))
  );
}

export function extractErrorMessages(response: any) {
  return response.errors?.map((e) => e.message) ?? [response.message];
}

export function updateResultToErrorMessage(updateResult: ScheduleUpdateResult) {
  if (updateResult.result.status === 'rejected') {
    const messages = extractErrorMessages(updateResult.result.reason);
    return `${updateResult.label}: ${messages.join('; ')}`;
  }
  return null;
}

const regionNameLookup: { [regionCode: string]: string } = libRegions
  .getRegionNamesAndCode('luxuryescapes')
  .reduce((lookup, region) => {
    lookup[region.code] = region.name;
    return lookup;
  }, {});

export const getRegionName = (regionCode: string) => regionNameLookup[regionCode] ?? '';

export function getLEDBrandName(brandValue: string) {
  return ledBrands.find((b) => b.value === brandValue)?.title ?? brandValue;
}

/**
 * Builds display maps for a multi-brand schedule table (e.g. Agent Hub + CVFR).
 *
 * Returns:
 * - `labelById`   — display label for each schedule id that should appear as a row
 * - `warningById` — inline warning message for out-of-sync rows
 */
export function buildScheduleDisplayMap(
  schedules: Array<App.Schedule>,
  brandList: Array<Brand>,
  brand: Brand,
): {
  labelById: Map<number, string>;
  warningById: Map<number, string>;
} {
  const labelById = new Map<number, string>();
  const warningById = new Map<number, string>();

  // Pre-compute which regions each brand supports so we can suppress
  // "CVFR missing" warnings for regions that sub-brands don't operate in
  // (e.g. Agent Hub supports GB/US but CVFR only supports AU/NZ).
  const validRegionsByBrand = new Map<Brand, Set<string>>();
  for (const b of brandList) {
    const regions = libRegions.getRegions(b).map((r) => r.code);
    validRegionsByBrand.set(b, new Set(regions));
  }

  const groups = new Map<string, Array<App.Schedule>>();
  for (const s of schedules) {
    if (!brandList.includes(s.brand as Brand)) {
      continue;
    }
    const key = `${s.type}:${s.region}`;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(s);
  }

  for (const group of groups.values()) {
    const region = group[0].region;
    const regionName = allRegions.find((r) => r.code === region)?.name ?? region;
    const primarySchedule = group.find((s) => s.brand === brand);
    const allBrandsPresent = group.length === brandList.length;
    const allBrandsSupportRegion = brandList.every((b) => validRegionsByBrand.get(b)?.has(region));
    const datesEqual =
      allBrandsPresent &&
      group.every(
        (s) =>
          new Date(s.start).getTime() === new Date(group[0].start).getTime() &&
          new Date(s.end).getTime() === new Date(group[0].end).getTime() &&
          !!s.restrict_purchase_to_lux_plus_members === !!group[0].restrict_purchase_to_lux_plus_members,
      );

    if (datesEqual) {
      // All brands in sync — one row, no warning
      const representative = primarySchedule ?? group[0];
      labelById.set(representative.id, regionName);
    } else if (!primarySchedule) {
      // Only sub-brand(s) — orphaned legacy record; delete to clean up
      for (const s of group) {
        labelById.set(s.id, `${regionName} (${brandsByValue[s.brand]?.title ?? s.brand})`);
        warningById.set(s.id, 'Orphaned — no Agent Hub counterpart. Delete this row to clean up.');
      }
    } else if (!allBrandsPresent) {
      if (allBrandsSupportRegion) {
        // Only primary brand in a region all sub-brands should cover
        warningById.set(primarySchedule.id, 'CVFR schedule missing — will sync automatically on next save.');
      }
      // If the region isn't supported by all brands (e.g. GB is AH-only), show the row
      // without any warning — the backend sync will never create a sub-brand schedule here.
      labelById.set(primarySchedule.id, regionName);
    } else {
      // Both present but dates differ — backend will align CVFR on next Agent Hub save
      for (const s of group) {
        labelById.set(s.id, `${regionName} (${brandsByValue[s.brand]?.title ?? s.brand})`);
        warningById.set(
          s.id,
          'Out of sync — CVFR dates differ. Will sync on next Agent Hub save, or delete to remove.',
        );
      }
    }
  }

  return { labelById, warningById };
}
